import React, { useEffect, useRef, useState } from "react";
import { Link, useLocation, useParams } from "react-router-dom";
import "./Iterinary.css";
import {
  categoryMeta,
  getTourSteps,
  getItineraryImages,
  regionData,
  slugify,
  Tour,
} from "../../data/tours";

type ItineraryState = {
  tour?: Tour;
  region?: string;
  category?: string;
  slug?: string;
};

type PackageItinerary = {
  slug: string;
  title: string;
  description: string;
  region: string;
  category: string;
  heroImage: string;
  mapImage: string;
  highlightImage: string;
  galleryImages: string[];
  ctaImage: string;
  overviewTitle: string;
  overviewText: string;
  highlightText: string;
  facts: {
    duration: string;
    distance: string;
    bestTime: string;
    level: string;
    price: string;
  };
  steps: string[];
};

const buildOverviewText = (tour: Tour, category: string, region: string) => {
  const focus =
    category === "Festival Tours"
      ? "ceremonies and cultural rhythm"
      : category === "Trekking & Hiking Tours"
      ? "highland trails and alpine panoramas"
      : category === "Fishing Expedition"
      ? "quiet riverbanks and pristine waters"
      : category === "Luxury Bhutan"
      ? "private comforts and curated experiences"
      : category === "Run in Bhutan"
      ? "scenic routes and active mornings"
      : category === "Camping & Outdoor Adventure"
      ? "campfire nights and outdoor immersion"
      : category === "Cycling in Bhutan"
      ? "winding climbs and thrilling descents"
      : category === "Off Road in the Himalayas"
      ? "rugged tracks and remote valleys"
      : "authentic encounters and flexible pacing";

  return `${tour.desc} Designed around ${focus}, this journey highlights the best of ${region}.`;
};

const buildHighlightText = (tour: Tour, region: string) =>
  `Guided by local experts, ${tour.title} blends signature moments in ${region} with slower, intimate discoveries across the valley.`;

const buildPackageIndex = (): Record<string, PackageItinerary> => {
  const index: Record<string, PackageItinerary> = {};

  for (const [regionName, region] of Object.entries(regionData)) {
    if (regionName === "All Regions") continue;
    for (const [category, tours] of Object.entries(region.categories)) {
      for (const tour of tours) {
        const slug = slugify(`${tour.title}-${regionName}`);
        const assets = getItineraryImages(slug);
        const fallbackFacts =
          categoryMeta[category] ?? categoryMeta["Private Tours"];
        const rawSteps = getTourSteps(category, regionName, tour.title);
        const days = rawSteps.length;
        const nights = Math.max(0, days - 1);
        const facts = {
          ...fallbackFacts,
          duration: `${nights} nights / ${days} days`,
          price: tour.price || fallbackFacts.price,
        };
        const steps = rawSteps.map(
          (step, index) => `Day ${String(index + 1).padStart(2, "0")}. ${step}`
        );
        const overviewTitle = `TIMELESS JOURNEYS THROUGH ${regionName.toUpperCase()}`;
        const overviewText = buildOverviewText(tour, category, regionName);
        index[slug] = {
          slug,
          title: tour.title,
          description: tour.desc,
          region: regionName,
          category,
          overviewTitle,
          overviewText,
          highlightText: buildHighlightText(tour, regionName),
          highlightImage: tour.img,
          facts,
          steps,
          heroImage: tour.img || assets.heroImage,
          mapImage: assets.mapImage,
          galleryImages: assets.galleryImages,
          ctaImage: assets.ctaImage,
        };
      }
    }
  }

  return index;
};

const packageIndex = buildPackageIndex();

function Itinerary() {
  const { slug } = useParams();
  const location = useLocation();
  const state = location.state as ItineraryState | null;
  const [activeStep, setActiveStep] = useState(0);
  const [availableGalleryImages, setAvailableGalleryImages] = useState<
    string[]
  >([]);
  const stepRefs = useRef<Array<HTMLLIElement | null>>([]);
  const planStepsRef = useRef<HTMLOListElement | null>(null);
  const handleImageFallback = (
    event: React.SyntheticEvent<HTMLImageElement>
  ) => {
    const target = event.currentTarget;
    if (target.dataset.fallback === "1") return;
    const src = target.getAttribute("src") ?? "";
    const nextSrc = src.endsWith(".png")
      ? src.replace(/\.png$/, ".jpg")
      : src.replace(/\.jpg$/, ".png");
    target.dataset.fallback = "1";
    target.setAttribute("src", nextSrc);
  };

  const stateSlug = state?.slug;
  const lookupSlug = (slug ?? stateSlug ?? "").toLowerCase();
  const itinerary = packageIndex[lookupSlug];

  const fallbackTitle = slug
    ? slug.replace(/-/g, " ").replace(/\b\w/g, (c) => c.toUpperCase())
    : "Bhutan Journey";
  const fallbackRegion = state?.region ?? "Bhutan";
  const fallbackCategory = state?.category ?? "Private Tours";
  const fallbackFacts =
    categoryMeta[fallbackCategory] ?? categoryMeta["Private Tours"];
  const fallbackSlug = slugify(`${fallbackTitle}-${fallbackRegion}`);
  const fallbackAssets = getItineraryImages(fallbackSlug);
  const fallbackRawSteps = getTourSteps(
    fallbackCategory,
    fallbackRegion,
    fallbackTitle
  );
  const fallbackDays = fallbackRawSteps.length;
  const fallbackNights = Math.max(0, fallbackDays - 1);

  const data: PackageItinerary = itinerary ?? {
    slug: fallbackSlug,
    title: state?.tour?.title ?? fallbackTitle,
    description:
      state?.tour?.desc ??
      "A tailored journey through Bhutan’s sacred valleys, crafted around the experiences you love most.",
    region: fallbackRegion,
    category: fallbackCategory,
    overviewTitle: `TIMELESS JOURNEYS THROUGH ${fallbackRegion.toUpperCase()}`,
    overviewText: buildOverviewText(
      {
        title: fallbackTitle,
        desc:
          state?.tour?.desc ??
          "Discover Bhutan at a gentle pace with curated cultural encounters.",
        img: "",
        price: fallbackFacts.price,
      },
      fallbackCategory,
      fallbackRegion
    ),
    highlightText: buildHighlightText(
      {
        title: fallbackTitle,
        desc: "",
        img: "",
        price: fallbackFacts.price,
      },
      fallbackRegion
    ),
    highlightImage: state?.tour?.img ?? fallbackAssets.highlightImage,
    facts: {
      ...fallbackFacts,
      duration: `${fallbackNights} nights / ${fallbackDays} days`,
    },
    steps: fallbackRawSteps.map(
      (step, index) => `Day ${String(index + 1).padStart(2, "0")}. ${step}`
    ),
    heroImage: state?.tour?.img ?? fallbackAssets.heroImage,
    mapImage: fallbackAssets.mapImage,
    galleryImages: fallbackAssets.galleryImages,
    ctaImage: fallbackAssets.ctaImage,
  };
  const regionMap = regionData[data.region]?.map ?? data.mapImage;
  const resolvedMapImage =
    regionMap === "/img/maps/bhutan_overview.svg" ? data.mapImage : regionMap;
  const isSingleMap = resolvedMapImage.includes("single");

  useEffect(() => {
    let rafId = 0;
    const updateActiveStep = () => {
      if (!planStepsRef.current) return;
      const listRect = planStepsRef.current.getBoundingClientRect();
      const scrollY = window.scrollY || window.pageYOffset;
      const listTop = listRect.top + scrollY;
      const items = stepRefs.current.filter(Boolean) as HTMLLIElement[];
      if (!items.length) return;

      const viewportHeight =
        window.innerHeight || document.documentElement.clientHeight || 1;
      const focusY = scrollY + viewportHeight * 0.4;

      let nextIndex = 0;
      items.forEach((item, idx) => {
        const itemTop = listTop + item.offsetTop;
        if (focusY >= itemTop) {
          nextIndex = idx;
        }
      });

      setActiveStep((prev) => (prev === nextIndex ? prev : nextIndex));
    };

    const onScroll = () => {
      cancelAnimationFrame(rafId);
      rafId = requestAnimationFrame(updateActiveStep);
    };

    setActiveStep(0);
    updateActiveStep();
    window.addEventListener("scroll", onScroll, { passive: true });
    window.addEventListener("resize", onScroll);
    return () => {
      cancelAnimationFrame(rafId);
      window.removeEventListener("scroll", onScroll);
      window.removeEventListener("resize", onScroll);
    };
  }, [data.steps.length]);

  useEffect(() => {
    let isActive = true;
    const loadImage = (primary: string, fallback: string) =>
      new Promise<string | null>((resolve) => {
        const img = new Image();
        img.onload = () => resolve(primary);
        img.onerror = () => {
          const fallbackImg = new Image();
          fallbackImg.onload = () => resolve(fallback);
          fallbackImg.onerror = () => resolve(null);
          fallbackImg.src = fallback;
        };
        img.src = primary;
      });

    const resolveGallery = async () => {
      const results = await Promise.all(
        data.galleryImages.map((src) => {
          const fallback = src.endsWith(".png")
            ? src.replace(/\.png$/, ".jpg")
            : src.replace(/\.jpg$/, ".png");
          return loadImage(src, fallback);
        })
      );
      if (isActive) {
        setAvailableGalleryImages(results.filter(Boolean) as string[]);
      }
    };

    resolveGallery();
    return () => {
      isActive = false;
    };
  }, [data.galleryImages]);

  return (
    <>
      <section
        className="itinerary-hero"
        style={{ backgroundImage: `url(${data.heroImage})` }}
      >
        <div className="itinerary-hero__overlay">
          <p className="itinerary-hero__eyebrow">Explore the extraordinary</p>
          <h1>{data.title}</h1>
        </div>
      </section>

      <section className="journey-section">
        <div className="journey-content">
          <div className="journey-copy">
            <Link to="/packages" className="journey-back">
              ← Back to packages
            </Link>
            <p className="journey-eyebrow">Explore the extraordinary</p>
            <h2>{data.overviewTitle}</h2>
            <p>{data.overviewText}</p>
            <ul className="journey-facts">
              <li>Trip duration: {data.facts.duration}</li>
              <li>Total distance: {data.facts.distance}</li>
              <li>Best time to travel: {data.facts.bestTime}</li>
              <li>Level: {data.facts.level}</li>
              <li>Cost: {data.facts.price}</li>
            </ul>
          </div>
          <div className="journey-map">
            <div
              className="journey-map__frame"
              data-region={data.region}
              data-single={isSingleMap ? "true" : "false"}
            >
              <img
                src={resolvedMapImage}
                alt={`${data.region} dzongkhag map`}
                className="journey-map__image"
              />
            </div>
          </div>
        </div>
      </section>

      <section className="plan-section">
        <div className="plan-header">
          <span className="plan-line" />
          <p className="plan-label">YOUR JOURNEY</p>
          <span className="plan-line" />
        </div>
        <h2 className="plan-title">PLANNED ITINERARY</h2>

        <div className="plan-grid">
          <ol className="plan-steps" ref={planStepsRef}>
            {data.steps.map((step, idx) => (
              <li
                key={step}
                className={`plan-step${activeStep === idx ? " is-active" : ""}`}
                data-step-idx={idx}
                ref={(el) => {
                  stepRefs.current[idx] = el;
                }}
              >
                <span className="plan-step__badge">{idx + 1}</span>
                <span className="plan-step__text">{step}</span>
              </li>
            ))}
          </ol>
          {/* <div className="plan-highlight">
            <img src={data.highlightImage} alt={data.title} />
            <div className="plan-highlight__text">
              <h3>{data.title}</h3>
              <p>{data.highlightText}</p>
            </div>
          </div> */}
        </div>
      </section>

      <section className="itinerary-gallery">
        <div className="gallery-grid">
          {availableGalleryImages.map((image, index) => (
            <img
              key={image}
              src={image}
              alt={`${data.title} gallery ${index + 1}`}
              onError={handleImageFallback}
            />
          ))}
        </div>
      </section>
    </>
  );
}

export default Itinerary;
